/**
 * Notification Setup — the tenant's per-event notification configuration.
 *
 * Replaces the old Notification Defaults tab, which wrote
 * `PlatformSettings.default_notification_preferences` — a field nothing in the
 * backend ever read. Every control here lands on a row the emit pipeline
 * resolves against on the next notification (scope doc §D).
 *
 * The grid is driven entirely by `GET /notifications/event-types/`: one row per
 * shipped event, grouped by module, with the tenant's override merged in by the
 * same resolver the emitter uses. Nothing about the taxonomy is hardcoded here,
 * so an event shipped in a later data migration appears with its defaults and
 * no override the next time this tab is opened.
 *
 * Save model: master switches apply immediately (see NotificationMasterCard);
 * per-event edits stage and go out on "Save changes".
 *
 * The header states which company the server resolved this configuration to,
 * and how (`meta.company_name` / `meta.company_scope_source`). It is the only
 * place the screen makes a claim about scope, and it used to make none — a
 * platform operator could read a division's name off the top bar while every
 * read and write landed on their own company's row.
 *
 * Known gap: the per-event save fans out one upsert per changed row rather than
 * using the single bulk round trip, because `notificationsAPI.bulkSaveOverrides`
 * currently targets `PUT /template-overrides/bulk/` with an `{overrides, …}`
 * body while the API exposes `POST /template-overrides/bulk_update/` taking
 * `{rows: […]}`. `POST /template-overrides/` is an upsert keyed on
 * `template_key` and matches field-for-field, so it is what this screen uses
 * until the two are reconciled in `lib/api/notifications.ts` (owned elsewhere).
 */
import { useEffect, useMemo, useState } from 'react';
import { isCustomerSideUser } from '../../../../lib/adminSetupAccess';
import { useAuth } from '../../../../contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Input } from '../../../ui/input';
import {
  AlertCircle,
  BellOff,
  Building2,
  Loader2,
  RefreshCw,
  Route,
  Save,
  Search,
  ShieldAlert,
} from 'lucide-react';
import type { PlatformSettings } from '../types';
import { NotificationDeliveryCard } from './NotificationDeliveryCard';
import { NotificationDomainSection } from './NotificationDomainSection';
import { NotificationMasterCard } from './NotificationMasterCard';
import { NotificationSendBudgetCard } from './NotificationSendBudgetCard';
import { SupplierScorecardCard } from './SupplierScorecardCard';
import { useNotificationSetup } from './useNotificationSetup';
import { describeScope } from './types';
import type { SetupEvent } from './types';

const matches = (event: SetupEvent, term: string): boolean => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    event.name.toLowerCase().includes(needle)
    || event.key.toLowerCase().includes(needle)
    || event.subdomainLabel.toLowerCase().includes(needle)
    || event.description.toLowerCase().includes(needle)
  );
};

interface NotificationSetupTabProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
  /**
   * Reports how many rows are staged but unsaved, so the screen hosting this
   * tab can guard an in-page section switch the same way `beforeunload` guards
   * leaving the page. Reports 0 on unmount, so a host never holds a stale
   * count for a tab that is no longer open.
   *
   * @link useUnsavedChangesGuard
   */
  onUnsavedChangesChange?: (count: number) => void;
}

export function NotificationSetupTab({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
  onUnsavedChangesChange,
}: NotificationSetupTabProps) {
  const setup = useNotificationSetup();
  // CR-8217: the master switches are install-wide, and the alert addresses
  // save through Admin Setup — both are refused to anyone but the customer side.
  const { user } = useAuth();
  const canSetInstallSwitches = isCustomerSideUser(user);
  const [search, setSearch] = useState('');
  const [openDomains, setOpenDomains] = useState<Record<string, boolean>>({});

  // Open the first module by default so the screen is never a wall of headers.
  // Only on the first load — a save reloads the catalogue and must not collapse
  // whatever the admin had expanded.
  useEffect(() => {
    const first = setup.catalogue?.domains[0];
    if (!first) return;
    setOpenDomains((current) =>
      Object.keys(current).length ? current : { [first.domain]: true },
    );
  }, [setup.catalogue]);

  // Unsaved-changes guard: the grid can hold a lot of staged work.
  useEffect(() => {
    if (setup.dirtyCount === 0) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [setup.dirtyCount]);

  // The other half of that guard. `beforeunload` only fires when the *page*
  // goes; Admin Setup switches section in place, unmounting this tab and every
  // staged row with it, so the host is told what is at stake and asks first.
  useEffect(() => {
    onUnsavedChangesChange?.(setup.dirtyCount);
    return () => onUnsavedChangesChange?.(0);
  }, [setup.dirtyCount, onUnsavedChangesChange]);

  const sections = useMemo(
    () =>
      (setup.catalogue?.domains ?? []).map((section) => ({
        ...section,
        visible: section.events.filter((event) => matches(event, search)),
      })),
    [setup.catalogue, search],
  );

  if (setup.loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading notification events…
        </CardContent>
      </Card>
    );
  }

  if (setup.error) {
    return (
      <Card>
        <CardContent className="space-y-4 py-12 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">{setup.error}</p>
          <Button variant="outline" onClick={() => void setup.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const hasEvents = (setup.catalogue?.eventCount ?? 0) > 0;
  const anyVisible = sections.some((section) => section.visible.length > 0);
  // Whose configuration this is, as the server resolved it — not as the top bar
  // or this component guessed it. Null only when the payload named no company.
  // Batched mail needs both switches, as the backend's channel_allowed('digest').
  const batchedMail = setup.master.digestEnabled && setup.master.emailsEnabled;
  const scopeStatement = describeScope(
    setup.catalogue?.companyName ?? '',
    setup.catalogue?.scopeSource ?? '',
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Notification Setup</h2>
          <p className="text-sm text-muted-foreground">
            Choose which events notify this tenant, on which channels, and how often.
            {setup.catalogue && ` ${setup.catalogue.eventCount} events, ${setup.catalogue.overriddenCount} customised.`}
          </p>
          {scopeStatement && (
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              {scopeStatement}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {setup.dirtyCount > 0 && (
            <>
              <Badge variant="secondary">
                {setup.dirtyCount} unsaved change{setup.dirtyCount === 1 ? '' : 's'}
              </Badge>
              <Button variant="ghost" disabled={setup.saving} onClick={setup.discardChanges}>
                Discard
              </Button>
            </>
          )}
          <Button
            disabled={!setup.canConfigure || setup.dirtyCount === 0 || setup.saving}
            onClick={() => void setup.saveChanges()}
          >
            {setup.saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      {!setup.canConfigure && (
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>
            Only company administrators can change notification configuration for this tenant.
          </AlertDescription>
        </Alert>
      )}

      {setup.catalogue?.baselineRouting && (
        <Alert>
          <Route className="h-4 w-4" />
          <AlertTitle>Owner-first routing is on</AlertTitle>
          <AlertDescription>
            Supplier events go to the order line&apos;s buyer or the NCR&apos;s owner as they happen.
            When there is none, the purchasing or quality department
            {batchedMail
              ? ' gets one email a day instead of one per event'
              : ' gets the bell only while batched email is off, never an email per event'}
            , and so does anyone else an event reaches without a named owner. Setting a
            notification&apos;s own frequency, or taking its bell away, changes that for it.
          </AlertDescription>
        </Alert>
      )}

      <NotificationMasterCard
        master={setup.master}
        canConfigure={setup.canConfigure}
        canSetInstallSwitches={canSetInstallSwitches}
        saving={setup.savingSettings}
        frequencyChoices={setup.catalogue?.frequencyChoices ?? []}
        frequencySummary={setup.frequencySummary}
        onChange={(patch, message) => void setup.saveSettings(patch, message)}
        onEmailsEnabledChange={(enabled) => void setup.setEmailsEnabled(enabled)}
        onDefaultFrequencyChange={(frequency) => void setup.setDefaultFrequency(frequency)}
        defaultFrequencyOthers={
          setup.catalogue?.baselineRouting ? (batchedMail ? 'daily' : 'bell') : null
        }
      />

      <NotificationDeliveryCard
        master={setup.master}
        canConfigure={setup.canConfigure && canSetInstallSwitches}
        saving={setup.savingSettings}
        onChange={(patch, message) => void setup.saveSettings(patch, message)}
        systemAlertEmails={platformSettings.systemAlertEmails}
        onSystemAlertEmailsChange={(value) =>
          onPlatformSettingsChange({ ...platformSettings, systemAlertEmails: value })
        }
        onSaveSystemAlertEmails={onSave}
        savingPlatform={saving}
      />

      <NotificationSendBudgetCard
        master={setup.master}
        canConfigure={setup.canConfigure}
        saving={setup.savingSettings}
        onChange={(patch, message) => void setup.saveSettings(patch, message)}
        company={setup.catalogue?.companyId ?? undefined}
      />

      <SupplierScorecardCard
        canConfigure={setup.canConfigure}
        emailsEnabled={setup.master.emailsEnabled}
      />

      {!hasEvents ? (
        <Card>
          <CardContent className="space-y-2 py-12 text-center">
            <BellOff className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No notification events available</p>
            <p className="text-sm text-muted-foreground">
              Nothing has been shipped to the event catalogue for this platform yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search events…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {!anyVisible ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No notification events match “{search}”.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {sections.map((section) => (
                <NotificationDomainSection
                  key={section.domain}
                  label={section.label}
                  // The filtered set, and the only set this section's bulk
                  // buttons may touch — a search must not reach past itself.
                  events={section.visible}
                  totalCount={section.events.length}
                  // A search implies "show me the matches" — sections open
                  // themselves while one is active, then fall back to the
                  // admin's own expand/collapse state.
                  open={search.trim() ? true : Boolean(openDomains[section.domain])}
                  onOpenChange={(open) =>
                    setOpenDomains((current) => ({ ...current, [section.domain]: open }))
                  }
                  canConfigure={setup.canConfigure}
                  frequencyChoices={setup.catalogue?.frequencyChoices ?? []}
                  defaultFrequency={setup.master.defaultFrequency}
                  emailsEnabled={setup.master.emailsEnabled}
                  digestEnabled={batchedMail}
                  configFor={setup.configFor}
                  isDirty={setup.isDirty}
                  onUpdate={setup.updateEvent}
                  onToggleChannel={setup.toggleChannel}
                  onReset={setup.resetEvent}
                  onSetEventsEnabled={setup.setEventsEnabled}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
